import { READ_ONLY_TOOLTIP_MESSAGE } from "@/utils/permissions";
import { formatNumberWithComma } from "@/utils/formatNumber";
import { type TransferEligibilityResult } from "@/features/salesforceIntegration/types";

export interface ResolveTransferActionStateArgs {
  isReadOnly: boolean;
  /** 判定APIの結果。未取得の間は`undefined`。 */
  eligibility:
    | Pick<TransferEligibilityResult, "isTransferable" | "reasonMessage">
    | undefined;
  isTransferring: boolean;
  /** ジョブの決着を検知し、判定APIを取り直している最中か。 */
  isAwaitingEligibilityRefresh: boolean;
  /** 取り直しに失敗し、古い判定結果しか持っていない状態か。 */
  hasEligibilityRefreshFailed: boolean;
  selectedRowCount: number;
  /** 実際に送る物件数。 */
  targetCount: number;
  /** 1回に送れる上限。判定APIの`maxTransferableCount`。 */
  maxTransferableCount: number | undefined;
}

export interface TransferActionState {
  isDisabled: boolean;
  /** 非活性の理由。出さない場合は空文字。 */
  tooltipMessage: string;
}

/**
 * 操作メニューの「Salesforceへ連携する」の活性とツールチップを1か所で決める。
 *
 * 判定の優先順位は次のとおり（実装計画書7.4節の表）。
 *   1. 読み取り専用ユーザー
 *   2. 判定結果の取得前
 *   3. 判定APIが`isTransferable=false`を返した（実行中・連携済みもここに含まれる）
 *   4. 連携の受け付け中
 *   5. ジョブの決着後、判定APIを取り直している最中
 *   6. 取り直しに失敗し、古い判定結果しか持っていない
 *   7. 行を選んでいない
 *   8. 選んだ行に送れる物件が無い
 *   9. 送る物件数が上限を超えている
 *
 * 連携できない理由の文言はバックエンドの`reasonMessage`をそのまま使う。
 * 例外は読み取り専用と、4〜9の文言である。受け付け中かどうか・取り直しの進み具合・
 * 選択状況はサーバーが知り得ない画面側の状態のため、文言をフロントエンドに置くほかない。
 *
 * 4を空文字にすると、押せない理由が分からないまま待たされる。
 *
 * 5と6が要るのは、ジョブが終わってから判定APIの取り直しが終わるまでに間があるためである。
 * その間の判定結果は連携前のままで、送信済みの物件が「連携可」に見えてしまう。
 * 塞がないと、同じ物件をもう一度POSTできる。
 */
export const resolveTransferActionState = (
  args: ResolveTransferActionStateArgs
): TransferActionState => {
  const {
    isReadOnly,
    eligibility,
    isTransferring,
    isAwaitingEligibilityRefresh,
    hasEligibilityRefreshFailed,
    selectedRowCount,
    targetCount,
    maxTransferableCount,
  } = args;

  if (isReadOnly) {
    return { isDisabled: true, tooltipMessage: READ_ONLY_TOOLTIP_MESSAGE };
  }
  if (eligibility === undefined) {
    return { isDisabled: true, tooltipMessage: "" };
  }
  if (!eligibility.isTransferable) {
    return {
      isDisabled: true,
      tooltipMessage: eligibility.reasonMessage ?? "",
    };
  }
  if (isTransferring) {
    return {
      isDisabled: true,
      tooltipMessage: "Salesforceへ連携しています。完了までお待ちください。",
    };
  }
  if (isAwaitingEligibilityRefresh) {
    return {
      isDisabled: true,
      tooltipMessage: "連携結果を確認しています。しばらくお待ちください。",
    };
  }
  if (hasEligibilityRefreshFailed) {
    return {
      isDisabled: true,
      tooltipMessage:
        "連携結果を確認できませんでした。再確認してから操作してください。",
    };
  }
  if (selectedRowCount === 0) {
    return {
      isDisabled: true,
      tooltipMessage: "連携する明細を選択してください。",
    };
  }
  if (targetCount === 0) {
    return {
      isDisabled: true,
      tooltipMessage: "選択した明細に連携できる物件がありません。",
    };
  }
  if (
    maxTransferableCount !== undefined &&
    targetCount > maxTransferableCount
  ) {
    return {
      isDisabled: true,
      tooltipMessage: `連携できる物件が${formatNumberWithComma(
        targetCount
      )}件あります。1回に送れるのは${formatNumberWithComma(
        maxTransferableCount
      )}件までです。選択を減らしてください。`,
    };
  }

  return { isDisabled: false, tooltipMessage: "" };
};
